/**
 * useCollectionInventory - Hook for collection inventory lookups
 *
 * Provides efficient lookup functions to check if cards exist in the user's collection,
 * both for exact printings (set + collector number) and any version (by name).
 */
import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { enrichedCollectionAtom } from '@/features/collection/state/collectionAtom';

// ============================================================================
// Types
// ============================================================================

/** Minimal printing info from collection for import matching */
export interface OwnedPrinting {
  id: string;
  name: string;
  set: string;
  collector_number: string;
  quantity: number;
}

export interface InventoryLookup {
  /** Check if exact set+collector_number exists in collection */
  hasExact: (set: string, collectorNumber: string) => boolean;

  /** Get quantity owned of exact printing */
  getExactQty: (set: string, collectorNumber: string) => number;

  /** Check if any printing of card (by name) exists */
  hasAny: (cardName: string) => boolean;

  /** Get total quantity owned across all printings (by name) */
  getTotalQty: (cardName: string) => number;

  /**
   * Get an owned printing of a card by name (case-insensitive).
   * Returns the first owned printing found, or null if not owned.
   * Useful for import matching when no specific printing is requested.
   */
  getOwnedPrinting: (cardName: string) => OwnedPrinting | null;

  /**
   * Get all owned printings of a card by name (case-insensitive).
   * Returns empty array if not owned.
   */
  getAllOwnedPrintings: (cardName: string) => OwnedPrinting[];

  /** Whether collection data is loaded */
  isLoading: boolean;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook providing efficient collection inventory lookups.
 * Builds lookup maps from the enriched collection for O(1) access.
 */
export default function useCollectionInventory(): InventoryLookup {
  const enrichedCollection = useAtomValue(enrichedCollectionAtom);

  return useMemo(() => {
    // Build lookup maps
    const bySetNumber = new Map<string, number>();
    const byNameLower = new Map<string, number>();
    // Map: name_lower → array of owned printings (for selecting owned version on import)
    const printingsByName = new Map<string, OwnedPrinting[]>();

    for (const cc of enrichedCollection.data ?? []) {
      const { card, quantity } = cc;

      // Exact: "set:collector_number" → total qty
      const setKey = `${card.set}:${card.collector_number}`;
      bySetNumber.set(setKey, (bySetNumber.get(setKey) ?? 0) + quantity);

      // Any: name_lower → total qty across all printings
      const nameLower = card.name.toLowerCase();
      byNameLower.set(nameLower, (byNameLower.get(nameLower) ?? 0) + quantity);

      // Printings: name_lower → array of owned printings
      const printing: OwnedPrinting = {
        id: card.id,
        name: card.name,
        set: card.set,
        collector_number: card.collector_number,
        quantity,
      };
      const existing = printingsByName.get(nameLower);
      if (existing) {
        // Check if this exact printing already exists (same set+number)
        const existingIdx = existing.findIndex((p) => p.set === card.set && p.collector_number === card.collector_number);
        if (existingIdx >= 0) {
          // Combine quantities for same printing
          existing[existingIdx].quantity += quantity;
        } else {
          existing.push(printing);
        }
      } else {
        printingsByName.set(nameLower, [printing]);
      }
    }

    return {
      hasExact: (set: string, collectorNumber: string) => bySetNumber.has(`${set}:${collectorNumber}`),

      getExactQty: (set: string, collectorNumber: string) => bySetNumber.get(`${set}:${collectorNumber}`) ?? 0,

      hasAny: (cardName: string) => byNameLower.has(cardName.toLowerCase()),

      getTotalQty: (cardName: string) => byNameLower.get(cardName.toLowerCase()) ?? 0,

      getOwnedPrinting: (cardName: string): OwnedPrinting | null => {
        const printings = printingsByName.get(cardName.toLowerCase());
        return printings?.[0] ?? null;
      },

      getAllOwnedPrintings: (cardName: string): OwnedPrinting[] => {
        return printingsByName.get(cardName.toLowerCase()) ?? [];
      },

      isLoading: enrichedCollection.isLoading,
    };
  }, [enrichedCollection.data, enrichedCollection.isLoading]);
}
